import "@xyflow/react/dist/style.css";
import { Alert, Badge, Center, Group, Loader, Text, ThemeIcon } from "@mantine/core";
import { IconSitemap } from "@tabler/icons-react";
import {
    Background,
    BackgroundVariant,
    Controls,
    type Node,
    type NodeMouseHandler,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect } from "react";
import { BucketNode } from "./BucketNode";
import { useExpandGraph } from "./hooks/useExpandGraph";
import type { ExpandGraphProps, ExplodeNodeData } from "./types";
import { NodeKind, NodeRole } from "./types";

const nodeTypes = { [NodeKind.BUCKET]: BucketNode };
const defaultEdgeOptions = { type: "step", style: { stroke: "#5f3dc4", strokeWidth: 2 } };

const FocusRoot = ({ nodes }: { nodes: Node<ExplodeNodeData>[] }) => {
    const { setCenter } = useReactFlow();
    const root = nodes.find((node) => node.id === "root");
    const hasRoot = Boolean(root);
    const centerX = root ? root.position.x + 120 : 0;
    const centerY = root ? root.position.y + 150 : 0;
    useEffect(() => {
        if (!hasRoot) return;
        setCenter(centerX, centerY, { zoom: 0.85, duration: 0 });
    }, [hasRoot, centerX, centerY, setCenter]);
    return null;
};

const Canvas = ({ index, rootLabel, onOpen }: ExpandGraphProps) => {
    const { nodes, edges, fieldLabel, isLoading, isError } = useExpandGraph({
        index,
        rootLabel,
    });

    const handleClick = useCallback<NodeMouseHandler>(
        (_, node) => {
            const { role, selection } = (node as Node<ExplodeNodeData>).data;
            if (role === NodeRole.GROUP) onOpen(selection);
        },
        [onOpen],
    );

    if (isError) return <Alert color="red">Failed to load documents for this view.</Alert>;
    if (!fieldLabel)
        return (
            <Alert color="gray" variant="light">
                The grouped view isn't set up for this data source yet.
            </Alert>
        );
    if (isLoading)
        return (
            <Center h={240}>
                <Loader color="purple" />
            </Center>
        );

    const typeCount = Math.max(nodes.length - 1, 0);

    return (
        <div>
            <Group gap="xs" align="center" mb={8}>
                <ThemeIcon variant="light" color="purple" size="lg" radius="md">
                    <IconSitemap size={18} />
                </ThemeIcon>
                <Text fw={700} size="lg">
                    Document Types
                </Text>
                <Badge color="purple" variant="filled" size="md">
                    {typeCount}
                </Badge>
            </Group>
            <div
                style={{
                    height: "72vh",
                    width: "100%",
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                }}
            >
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    defaultEdgeOptions={defaultEdgeOptions}
                    onNodeClick={handleClick}
                    nodesDraggable={false}
                    panOnScroll
                    zoomOnScroll={false}
                    minZoom={0.3}
                    maxZoom={1.5}
                >
                    <FocusRoot nodes={nodes} />
                    <Background variant={BackgroundVariant.Dots} gap={24} color="#e9ecef" />
                    <Controls showInteractive={false} />
                </ReactFlow>
            </div>
        </div>
    );
};

export const ExpandGraph = (props: ExpandGraphProps) => (
    <ReactFlowProvider>
        <Canvas {...props} />
    </ReactFlowProvider>
);
